import { Component } from '@angular/core';

import { SVGIcon, xIcon } from '@progress/kendo-svg-icons';

import { UITier } from './models';
import {
  GridState,
  GridStateCompositeFilterDescriptor,
  LoaderService,
  MessageService,
  ValidationResultService,
} from '@shared';
import { debounceTime, distinctUntilChanged, Subject, tap } from 'rxjs';
import {
  AlertsService,
  GlobalEventsService,
  Restaurant,
  RestaurantService,
  SelectableRestaurant,
  Tier,
  TieredRestaurant,
  TiersService,
} from '@shared-data';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-tier',
  templateUrl: './tier.component.html',
  styleUrl: './tier.component.scss',
})
export class TierComponent {
  tieredRestaurants: TieredRestaurant[] = [];

  untieredRestaurants: SelectableRestaurant[] = [];

  tiers: UITier[] = [];

  addTierDialogOpen: boolean = false;

  tierToRemove: Tier | null = null;

  tierToUnlock: Tier | null = null;

  tierToSync: Tier | null = null;

  removeIcon: SVGIcon = xIcon;

  restaurantSearch: Subject<string> = new Subject<string>();

  searchText: string = '';

  restaurantToRemove: TieredRestaurant | null = null;

  unassignRestaurantDialogOpen: boolean = false;

  restaurantToSetKing: TieredRestaurant | null = null;

  state!: GridState;

  total: number = 0;

  pageSize: number = 100;

  selectedRestaurants: string[] = [];

  tierAlignEnabled = environment.tierAlignEnabled;

  constructor(
    private globalEventsService: GlobalEventsService,
    private loaderService: LoaderService,
    private restaurantsService: RestaurantService,
    private tierService: TiersService,
    private messageService: MessageService,
    private validationResultService: ValidationResultService,
  ) {}

  ngOnInit() {
    this.state = new GridState(this.pageSize);

    this.getTiers();

    this.getRestaurants();

    this.subscribeToRestaurantSearch();
  }

  getTiers() {
    this.loaderService.show();

    this.tierService.getTiers().subscribe({
      next: (tiers) => {
        tiers = tiers.map((tier) =>
          this.tierService.handleDocumentTypesForInput(tier),
        );

        this.tiers = tiers.map((tier) => new UITier(tier));
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(`Failed to get tiers: ${err.error}`);
        this.loaderService.hide();
      },
    });
  }

  getRestaurants() {
    this.fetchTieredRestaurants();
    this.fetchUntieredRestaurants();
  }

  fetchTieredRestaurants() {
    this.loaderService.show();
    this.restaurantsService.getTieredRestaurants({}).subscribe({
      next: (tieredRestaurants) => {
        this.tieredRestaurants = tieredRestaurants.data;
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(
          `Failed to get tiered restaurants: ${err.error}`,
        );
        this.loaderService.hide();
      },
    });
  }

  fetchUntieredRestaurants() {
    this.loaderService.show();
    this.restaurantsService.getUntieredRestaurants(this.state).subscribe({
      next: (restaurants) => {
        this.untieredRestaurants = restaurants.data.map((restaurant) => {
          const selectableRestaurant = new SelectableRestaurant(restaurant);
          selectableRestaurant.selected =
            this.selectedRestaurants.indexOf(restaurant.id) >= 0;
          return selectableRestaurant;
        });

        this.total = restaurants.total;

        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(
          `Failed to get untiered restaurants: ${err.error}`,
        );
        this.loaderService.hide();
      },
    });
  }

  subscribeToRestaurantSearch() {
    this.restaurantSearch
      .pipe(
        debounceTime(300),
        distinctUntilChanged(),
        tap((searchText) => {
          this.searchText = searchText;

          this.state.resetPager(this.pageSize);

          this.state.filter = new GridStateCompositeFilterDescriptor()
            .or({
              field: 'name',
              operator: 'contains',
              value: this.searchText,
            })
            .or({
              field: 'dmaName',
              operator: 'contains',
              value: this.searchText,
            })
            .or({
              field: 'cityName',
              operator: 'contains',
              value: this.searchText,
            });

          this.fetchUntieredRestaurants();
        }),
      )
      .subscribe();
  }

  handleFilter(enteredValue: any) {
    this.restaurantSearch.next(enteredValue);
  }

  addTier(tierName: string | null) {
    this.addTierDialogOpen = false;
    if (tierName != null) {
      this.loaderService.show();
      this.tierService.createTier(tierName).subscribe({
        next: (tier) => {
          const newTier = new UITier(tier);

          this.tiers.push(newTier);

          this.globalEventsService.updateTiers();

          this.getTiers();

          this.loaderService.hide();
        },
        error: (err) => {
          const validationError = err?.error;
          if (validationError) {
            this.messageService.error(validationError);
          }
          this.loaderService.hide();
        },
      });
    }
  }

  assignRestaurants() {
    const selectedRestaurants = this.untieredRestaurants.filter(
      (restaurant) => restaurant.selected,
    );
    const selectedTierId = this.tiers.find((tier) => tier.selected)?.id;

    if (selectedTierId == null) {
      this.messageService.error(
        'Please select a tier to assign restaurants to',
      );
      return;
    }

    const tieredRestaurants = selectedRestaurants.map(
      (restaurant) => new TieredRestaurant(restaurant, selectedTierId, false),
    );

    const restaurantsInTier = this.tieredRestaurants.filter(
      (restaurant) => restaurant.priceTierId == selectedTierId,
    );

    if (!restaurantsInTier.some((restaurant) => restaurant.isKing)) {
      tieredRestaurants[0].isKing = true;
    }

    this.loaderService.show();
    this.restaurantsService.updateBulkRestaurants(tieredRestaurants).subscribe({
      next: () => {
        this.getRestaurants();
        this.globalEventsService.updateTiers();
        this.getTiers();
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(`Failed to assign restaurants: ${err.error}`);
        this.loaderService.hide();
      },
    });
  }

  showUnassignRestaurantDialog(tieredRestaurant: TieredRestaurant) {
    if (tieredRestaurant.isKing) {
      this.messageService.error(
        'Cannot unassign king restaurant, please set another restaurant as king before unassigning',
      );
      return;
    }
    this.restaurantToRemove = tieredRestaurant;
  }

  unassignRestaurant(restaurant: Restaurant | null) {
    this.restaurantToRemove = null;

    if (restaurant == null) return;

    this.loaderService.show();
    this.restaurantsService.updateRestaurant(restaurant).subscribe({
      next: () => {
        this.getRestaurants();
        this.globalEventsService.updateTiers();
        this.getTiers();
        this.loaderService.hide();
      },
      error: (err) => {
        this.messageService.error(
          `Failed to unassign restaurant: ${err.error}`,
        );
        this.loaderService.hide();
      },
    });
  }

  toggleTier(tierId: string) {
    this.tiers.forEach((tier) => {
      if (tier.id !== tierId && tier.selected) {
        tier.toggleSelect();
      } else if (tier.id === tierId && !tier.selected) {
        tier.toggleSelect();
      }
    });
  }

  toggleRestaurantSelect(restaurant: SelectableRestaurant) {
    // if (restaurant.parToken == null) return;
    restaurant.toggleSelect();

    if (restaurant.selected) {
      this.selectedRestaurants.push(restaurant.id);
    } else {
      this.selectedRestaurants = this.selectedRestaurants.filter(
        (id) => id !== restaurant.id,
      );
    }
  }

  onRemovedTierConfirm(confirmed: boolean) {
    if (confirmed) {
      this.getTiers();
      this.getRestaurants();

      this.globalEventsService.updateTiers();
    }

    this.tierToRemove = null;
  }

  onKingRestaurantSetConfirm(confirmed: boolean) {
    if (confirmed) {
      this.fetchTieredRestaurants();

      this.globalEventsService.updateTiers();
    }

    this.restaurantToSetKing = null;
  }

  onUnlockTierConfirm(confirmed: boolean) {
    if (confirmed) {
      this.getTiers();

      this.globalEventsService.updateTiers();

      this.globalEventsService.updatePendingChanges();
    }
    this.tierToUnlock = null;
  }

  updateTier(tier: Tier, errorCallback: (errorMessage: string) => void) {
    this.loaderService.show();
    this.tierService.updateTier(tier).subscribe({
      next: () => {
        this.loaderService.hide();

        this.globalEventsService.updateTiers();
      },
      error: (err) => {
        errorCallback(err.error);
        this.loaderService.hide();
      },
    });
  }

  lockTier(tier: Tier) {
    tier.isLocked = !tier.isLocked;

    this.updateTier(tier, (err) => {
      this.messageService.error(
        `Failed to ${tier.isLocked ? 'lock' : 'unlock'} tier`,
      );
      tier.isLocked = !tier.isLocked;
    });
  }

  toggleEdit(tier: UITier) {
    if (tier.editing) {
      this.updateTier(tier, (err) => {
        this.messageService.error(`Failed to update tier: ${err}`);

        this.getTiers();
      });
    }

    tier.toggleEdit();
  }

  showSetKingDialog(restaurant: TieredRestaurant) {
    this.restaurantToSetKing = restaurant;
  }

  onSyncTierConfirm(confirmed: boolean) {
    if (confirmed) {
      this.getTiers();

      this.globalEventsService.updateTiers();

      this.globalEventsService.updatePendingChanges();
    }
    this.tierToSync = null;
  }
}
